import { ArrowUpRight, Phone, Rows3, RotateCcw, Triangle, Square, Sparkles, Flame, DoorOpen } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type DoorType = {
  monogram: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  name: string;
  tagline: string;
  description: string;
  features: string[];
};
const SECTIONAL: DoorType = {
  monogram: "S",
  icon: Rows3,
  name: "Sectional",
  tagline: "Modern standard · Insulated panels",
  description: "Australia's most-installed door style. Horizontal insulated panels lift on tracks above the opening — quiet, secure, and built for daily cycling. Insulated options ideal for double-storey or attached homes.",
  features: ["Insulated R-rated", "Custom colours", "Smart-enabled", "Quiet operation", "20+ year lifespan"]
};
const OTHER_TYPES: DoorType[] = [{
  monogram: "R",
  icon: RotateCcw,
  name: "Roller",
  tagline: "Space-saving · Compact homes",
  description: "Coiled steel curtain rolls up into a compact box above the opening. Ideal for low-ceiling garages, granny flats, and Sydney homes where every centimetre counts.",
  features: ["Compact roll-up", "Quiet motor", "Insulated available", "Auto-lock"]
}, {
  monogram: "T",
  icon: Triangle,
  name: "Tilt-A-Door",
  tagline: "Classic Australian · Cost-effective",
  description: "Single rigid panel tilts up and slides into the ceiling space. The traditional Aussie garage door — sturdy, simple, lower entry price point.",
  features: ["Heritage-friendly", "Custom skins", "Low maintenance", "Wide skin range"]
}, {
  monogram: "P",
  icon: Square,
  name: "Panel-Lift",
  tagline: "Architect's pick · Premium aesthetic",
  description: "Modern minimalist single-panel door. Clean horizontal lines, no visible joins, premium street appeal. Available in oversized dimensions for tall garages.",
  features: ["Clean lines", "Tall door options", "Premium finishes", "Modern look"]
}, {
  monogram: "C",
  icon: Sparkles,
  name: "Custom / Bespoke",
  tagline: "One-of-a-kind · Made to brief",
  description: "Timber-look, paint-matched to render, oversized, heritage replica, anything within reason. We work with manufacturers on full custom builds for renovations and new builds.",
  features: ["Custom sizes", "Timber-look options", "Heritage replicas", "Paint-match"]
}];
export default function InstallationDoorTypes() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="installation-door-types-heading">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 15% 30%, ${NAVY_LIGHT}66 0%, transparent 50%), radial-gradient(ellipse at 85% 80%, ${NAVY_DEEP} 0%, transparent 60%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}80 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-14 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <DoorOpen className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Door Types We Install
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: "rgba(255,255,255,0.4)"
        }}>
            5 Types · Every Property · Sydney 2026
          </div>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 md:mb-16 items-end">
          <h2 id="installation-door-types-heading" className="lg:col-span-8 font-extrabold text-white" style={{
          fontFamily: FONT_HEAD,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            Every door type.{" "}
            <span style={{
            color: GOLD
          }}>Every property.</span>
            <br className="hidden md:block" />
            One install team.
          </h2>

          <p className="lg:col-span-4 text-[15px] md:text-base leading-relaxed" style={{
          color: "rgba(255,255,255,0.65)",
          fontFamily: FONT_BODY
        }}>
            From classic tilt to bespoke timber-look &mdash; we install the right
            door for your home, driveway, and budget. Free on-site measure on every quote.
          </p>
        </div>

        {/* === FEATURED SECTIONAL CARD === */}
        <FeaturedDoorCard door={SECTIONAL} />

        {/* === 4 OTHER TYPES in 2x2 grid === */}
        <div className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {OTHER_TYPES.map((door, idx) => <DoorTypeCard key={door.name} door={door} index={idx + 2} />)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 md:mt-20 relative overflow-hidden rounded-2xl border" style={{
        borderColor: `${GOLD}33`,
        background: `linear-gradient(135deg, ${NAVY_LIGHT}66 0%, ${NAVY} 100%)`
      }}>
          <span aria-hidden className="absolute top-3 left-3 w-4 h-4 border-l border-t" style={{
          borderColor: GOLD
        }} />
          <span aria-hidden className="absolute top-3 right-3 w-4 h-4 border-r border-t" style={{
          borderColor: GOLD
        }} />
          <span aria-hidden className="absolute bottom-3 left-3 w-4 h-4 border-l border-b" style={{
          borderColor: GOLD
        }} />
          <span aria-hidden className="absolute bottom-3 right-3 w-4 h-4 border-r border-b" style={{
          borderColor: GOLD
        }} />

          <div className="relative p-6 md:p-10 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-2" style={{
              color: GOLD
            }}>
                Not sure which type fits?
              </div>
              <h3 className="text-white font-bold" style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(1.375rem, 2.5vw, 1.875rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em"
            }}>
                Free 15-min consult &mdash; we&rsquo;ll match
                <br className="hidden md:block" />
                the right door to your home and budget.
              </h3>
            </div>

            <a href="tel:0485000933" className="group inline-flex items-center gap-3 rounded-full px-7 py-4 text-[15px] font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_BODY,
            boxShadow: `0 14px 40px -10px ${GOLD}99`
          }}>
              <Phone className="h-[18px] w-[18px]" strokeWidth={2.5} />
              Call 0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}80 50%, transparent 100%)`
    }} />
    </section>;
}

/* === FEATURED SECTIONAL CARD === */
function FeaturedDoorCard({
  door
}: {
  door: DoorType;
}) {
  const Icon = door.icon;
  return <article className="relative rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: "rgba(245,166,35,0.05)",
    borderColor: `${GOLD}40`,
    boxShadow: `0 16px 50px -20px ${GOLD}33`
  }}>
      <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{
      background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
    }} />

      {/* Big watermark "01" */}
      <div aria-hidden className="absolute -bottom-8 -right-2 font-extrabold leading-none select-none pointer-events-none" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}14`,
      fontSize: "clamp(8rem, 14vw, 11rem)",
      letterSpacing: "-0.05em",
      fontVariantNumeric: "tabular-nums"
    }}>
        01
      </div>

      <div className="relative p-5 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7">

        {/* LEFT: monogram + name + features */}
        <div className="md:col-span-5 flex flex-col">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl" style={{
            backgroundColor: GOLD,
            color: NAVY,
            boxShadow: `0 8px 24px -8px ${GOLD}80`
          }}>
              <Icon className="h-7 w-7" strokeWidth={2} />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-extrabold text-white mb-1" style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(1.25rem, 1.9vw, 1.5rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em"
            }}>
                {door.name}
              </h3>

              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-[0.18em] uppercase mb-2" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_BODY
            }}>
                <Flame className="h-2.5 w-2.5" strokeWidth={2.5} />
                Most Popular
              </span>

              <p className="text-[12px] font-medium" style={{
              color: "rgba(255,255,255,0.6)",
              fontFamily: FONT_BODY
            }}>
                {door.tagline}
              </p>
            </div>
          </div>

          {/* Features list */}
          <div className="rounded-lg overflow-hidden border mt-auto" style={{
          backgroundColor: "rgba(8,20,56,0.45)",
          borderColor: `${GOLD}33`
        }}>
            <div className="flex items-stretch">
              <div aria-hidden className="w-[3px] flex-shrink-0" style={{
              backgroundColor: GOLD
            }} />
              <div className="px-4 py-3 w-full">
                <div className="text-[9.5px] font-bold tracking-[0.22em] uppercase mb-2" style={{
                color: GOLD
              }}>
                  Key features
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {door.features.map(f => <span key={f} className="text-[11px] font-semibold rounded px-2 py-1 border" style={{
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderColor: "rgba(245,166,35,0.30)",
                  color: "rgba(255,255,255,0.92)",
                  fontFamily: FONT_BODY
                }}>
                      {f}
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: description */}
        <div className="md:col-span-7">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-3" style={{
          color: GOLD
        }}>
            What it is
          </div>
          <p className="text-[15px] leading-[1.65] mb-5" style={{
          color: "rgba(255,255,255,0.82)"
        }}>
            {door.description}
          </p>

          <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-2" style={{
          color: "rgba(255,255,255,0.45)"
        }}>
            Best for
          </div>
          <p className="text-[14px] leading-[1.55]" style={{
          color: "rgba(255,255,255,0.7)"
        }}>
            Modern homes, double-storey or attached living, families wanting
            quiet daily operation, anyone planning for the long term.
          </p>
        </div>
      </div>
    </article>;
}

/* === COMPACT DOOR TYPE CARD === */
function DoorTypeCard({
  door,
  index
}: {
  door: DoorType;
  index: number;
}) {
  const Icon = door.icon;
  return <article className="group relative rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: "rgba(255,255,255,0.025)",
    borderColor: "rgba(255,255,255,0.08)"
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = `${GOLD}66`;
    e.currentTarget.style.backgroundColor = "rgba(245,166,35,0.04)";
    e.currentTarget.style.transform = "translateY(-2px)";
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.025)";
    e.currentTarget.style.transform = "translateY(0)";
  }}>
      {/* Hover top accent */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`
    }} />

      {/* Watermark number */}
      <div aria-hidden className="absolute -bottom-3 -right-1 font-extrabold leading-none select-none pointer-events-none" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}0F`,
      fontSize: "clamp(4rem, 7vw, 5.5rem)",
      letterSpacing: "-0.04em",
      fontVariantNumeric: "tabular-nums"
    }}>
        {String(index).padStart(2, "0")}
      </div>

      <div className="relative p-5 md:p-6">

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 group-hover:scale-105" style={{
          backgroundColor: `${GOLD}1F`,
          border: `1px solid ${GOLD}40`
        }}>
            <Icon className="h-5 w-5" strokeWidth={2} style={{
            color: GOLD
          } as React.CSSProperties} />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-white transition-colors duration-200 group-hover:text-[#F5A623]" style={{
            fontFamily: FONT_HEAD,
            fontSize: "1.125rem",
            lineHeight: 1.2,
            letterSpacing: "-0.018em"
          }}>
              {door.name}
            </h3>
            <p className="text-[12px] font-medium mt-0.5 leading-snug" style={{
            color: "rgba(255,255,255,0.5)",
            fontFamily: FONT_BODY
          }}>
              {door.tagline}
            </p>
          </div>

          <ArrowUpRight className="flex-shrink-0 mt-1 h-4 w-4 opacity-25 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} style={{
          color: GOLD
        } as React.CSSProperties} />
        </div>

        {/* Description */}
        <p className="text-[13.5px] leading-[1.55] mb-4" style={{
        color: "rgba(255,255,255,0.68)"
      }}>
          {door.description}
        </p>

        {/* Features pills */}
        <div className="flex flex-wrap gap-1.5">
          {door.features.map(f => <span key={f} className="text-[10.5px] font-semibold rounded px-2 py-1 border" style={{
          backgroundColor: "rgba(255,255,255,0.04)",
          borderColor: "rgba(255,255,255,0.10)",
          color: "rgba(255,255,255,0.78)",
          fontFamily: FONT_BODY
        }}>
              {f}
            </span>)}
        </div>
      </div>
    </article>;
}